export const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value))

export const lerp = (a: number, b: number, amount: number): number => a + (b - a) * amount

/**
 * Interpolates a sampled series without overshooting the current segment.
 * Unlike per-segment easing, the adjacent samples provide continuous motion
 * through minute boundaries when many market minutes are compressed in time.
 */
export function monotoneCubicInterpolate(
  previous: number,
  current: number,
  next: number,
  following: number,
  amount: number,
): number {
  const t = clamp(amount, 0, 1)
  const delta = next - current
  if (Math.abs(delta) < Number.EPSILON) return current

  let currentSlope = (next - previous) * 0.5
  let nextSlope = (following - current) * 0.5
  let alpha = currentSlope / delta
  let beta = nextSlope / delta

  if (alpha < 0) {
    currentSlope = 0
    alpha = 0
  }
  if (beta < 0) {
    nextSlope = 0
    beta = 0
  }
  const magnitude = alpha * alpha + beta * beta
  if (magnitude > 9) {
    const scale = 3 / Math.sqrt(magnitude)
    currentSlope = scale * alpha * delta
    nextSlope = scale * beta * delta
  }

  const t2 = t * t
  const t3 = t2 * t
  return (2 * t3 - 3 * t2 + 1) * current
    + (t3 - 2 * t2 + t) * currentSlope
    + (-2 * t3 + 3 * t2) * next
    + (t3 - t2) * nextSlope
}

export function easeInOutCubic(value: number): number {
  return value < 0.5 ? 4 * value * value * value : 1 - Math.pow(-2 * value + 2, 3) / 2
}

export function easeOutBack(value: number): number {
  const c1 = 1.18
  const c3 = c1 + 1
  return 1 + c3 * Math.pow(value - 1, 3) + c1 * Math.pow(value - 1, 2)
}

export function hashString(value: string): number {
  let hash = 2166136261
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

export function mulberry32(seed: number): () => number {
  return () => {
    let value = (seed += 0x6d2b79f5)
    value = Math.imul(value ^ (value >>> 15), value | 1)
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61)
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296
  }
}

export function radiusForFlow(value: number, minRadius: number, maxRadius: number): number {
  const yi = Math.abs(value) / 100_000_000
  const normalized = clamp(Math.log1p(yi) / Math.log1p(130), 0, 1)
  return lerp(minRadius, maxRadius, Math.pow(normalized, 1.38))
}
